import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ArrowLeftRight, Upload, Loader2, CheckCircle2, XCircle, Link2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Rapprochement() {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [releves, setReleves] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [ecritures, setEcritures] = useState([]);
  const [synthese, setSynthese] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState('non_rapproches');

  useEffect(() => {
    axios.get(`${API_URL}/bank-accounts`).then(r => {
      setBankAccounts(r.data);
      if (r.data.length > 0) setSelectedAccount(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/rapprochement/releves`, { params: { bank_account_id: selectedAccount } }),
      axios.get(`${API_URL}/rapprochement/mouvements`, { params: { bank_account_id: selectedAccount, is_rapproche: tab === 'rapproches' } }),
      axios.get(`${API_URL}/rapprochement/synthese/${selectedAccount}`),
      axios.get(`${API_URL}/comptabilite/ecritures`, { params: { journal_code: 'BQ', is_validee: false, limit: 500 } }),
    ]).then(([rel, mvt, syn, ecr]) => {
      setReleves(rel.data);
      setMouvements(mvt.data);
      setSynthese(syn.data);
      setEcritures(ecr.data);
    }).catch(() => toast.error('Erreur chargement'))
    .finally(() => setLoading(false));
  }, [selectedAccount, tab]);

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAccount) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank_account_id', selectedAccount);
      formData.append('solde_debut', '0');
      formData.append('solde_fin', '0');
      const res = await axios.post(`${API_URL}/rapprochement/releves/import-csv`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.nb_mouvements} mouvements importés`);
      setImportOpen(false);
      // Refresh
      setTab('non_rapproches');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur import'); }
    finally { setImporting(false); }
  };

  const handleRapprocher = async (mouvementId, ecritureId) => {
    try {
      await axios.post(`${API_URL}/rapprochement/rapprocher`, { actions: [{ mouvement_id: mouvementId, ecriture_id: ecritureId }] });
      toast.success('Mouvement rapproché');
      // Refresh
      setTab(tab); // triggers re-fetch
    } catch (e) { toast.error('Erreur rapprochement'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <ArrowLeftRight className="w-6 h-6 inline mr-2" />Rapprochement bancaire
          </h1>
        </div>
        <div className="flex gap-2">
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Compte bancaire" /></SelectTrigger>
            <SelectContent>{bankAccounts.map(ba => <SelectItem key={ba.id} value={ba.id}>{ba.label} - {ba.iban?.slice(-4)}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" /> Importer relevé
          </Button>
        </div>
      </div>

      {/* Synthèse */}
      {synthese && (
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6 text-center">
            <p className="text-2xl font-bold">{synthese.total_mouvements}</p><p className="text-sm text-slate-500">Mouvements total</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <p className="text-2xl font-bold text-green-600">{synthese.rapproches}</p><p className="text-sm text-slate-500">Rapprochés</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <p className="text-2xl font-bold text-amber-600">{synthese.non_rapproches}</p><p className="text-sm text-slate-500">Non rapprochés</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <Progress value={synthese.pourcentage_rapproche} className="mb-2" />
            <p className="text-center text-sm text-slate-500">{synthese.pourcentage_rapproche}% rapproché</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="non_rapproches">Non rapprochés</TabsTrigger>
          <TabsTrigger value="rapproches">Rapprochés</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Date</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="w-32 text-right">Montant</TableHead>
                  <TableHead className="w-24">Réf.</TableHead>
                  <TableHead className="w-32">Statut</TableHead>
                  {tab === 'non_rapproches' && <TableHead className="w-32">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mouvements.map(m => (
                  <TableRow key={m.id}>
                    <TableCell className="text-sm">{m.date_operation}</TableCell>
                    <TableCell>{m.libelle}</TableCell>
                    <TableCell className={`text-right font-mono font-medium ${m.montant >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {m.montant >= 0 ? '+' : ''}{formatCurrency(m.montant)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{m.reference || ''}</TableCell>
                    <TableCell>
                      {m.is_rapproche
                        ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Rapproché</Badge>
                        : <Badge className="bg-amber-100 text-amber-700"><XCircle className="w-3 h-3 mr-1" />À rapprocher</Badge>
                      }
                    </TableCell>
                    {tab === 'non_rapproches' && (
                      <TableCell>
                        <Select onValueChange={v => handleRapprocher(m.id, v)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Lier..." /></SelectTrigger>
                          <SelectContent className="max-h-48">
                            {ecritures.filter(e => Math.abs(e.total_debit - Math.abs(m.montant)) < 0.01 || Math.abs(e.total_credit - Math.abs(m.montant)) < 0.01).map(e => (
                              <SelectItem key={e.id} value={e.id}>
                                <span className="text-xs">n°{e.numero_ecriture} - {e.libelle} ({formatCurrency(e.total_debit || e.total_credit)})</span>
                              </SelectItem>
                            ))}
                            {ecritures.length > 0 && <SelectItem value="__sep" disabled>── Toutes ──</SelectItem>}
                            {ecritures.slice(0, 20).map(e => (
                              <SelectItem key={`all-${e.id}`} value={e.id}>
                                <span className="text-xs">n°{e.numero_ecriture} - {e.libelle}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {mouvements.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">Aucun mouvement</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Upload className="w-5 h-5 inline mr-2" />Importer un relevé bancaire</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Importez un fichier CSV de votre banque (format : Date;Libellé;Montant ou Date;Libellé;Débit;Crédit)</p>
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            {importing ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : (
              <label className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-600">Cliquez pour choisir un fichier CSV</p>
                <input type="file" className="hidden" accept=".csv" onChange={handleImportCSV} />
              </label>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
